import React, { useCallback, useEffect, useState } from 'react';
import {
  FaClipboardList,
  FaExclamationTriangle,
  FaSearch,
  FaSpinner,
} from 'react-icons/fa';

const PAGE_SIZE = 10;
const NEW_USER_DAYS = 7;

function parseDate(value) {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getUserPhone(user) {
  const candidates = [user.phone, user.contact, user.phoneNumber, user.mobile, user.mobileNumber]
    .filter(Boolean)
    .map(String);
  return candidates[0] || '';
}

function getUserJoinedAt(user) {
  return user.createdAt || user.created_at || user.joinedAt || null;
}

function sortUsersNewestFirst(users) {
  return [...users].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
}

function isRecentlyAdded(user) {
  const date = parseDate(getUserJoinedAt(user));
  if (!date) return false;
  const cutoff = Date.now() - NEW_USER_DAYS * 24 * 60 * 60 * 1000;
  return date.getTime() >= cutoff;
}

function getTotalPages(total, limit) {
  if (!total || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / limit));
}

function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  return [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function AdminUsersPanel({ user, onTotalChange }) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isSearchPending = searchInput.trim() !== search;

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (search) {
        params.set('search', search);
      }

      const response = await fetch(
        `${process.env.REACT_APP_HOST_SERVER}/api/admin/users?${params.toString()}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (response.status === 403) {
        throw new Error('You do not have admin access.');
      }
      if (!response.ok) {
        throw new Error('Unable to load users.');
      }

      const json = await response.json();
      if (!json.successful) {
        throw new Error(json.message || 'Unable to load users.');
      }

      const nextItems = sortUsersNewestFirst(json.data?.items || []);
      const nextPagination = json.data?.pagination || { page, limit: PAGE_SIZE, total: nextItems.length };

      setItems(nextItems);
      setPagination(nextPagination);
      if (!search && typeof onTotalChange === 'function') {
        onTotalChange(nextPagination.total || 0);
      }
    } catch (err) {
      setItems([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
      setError(err.message || 'Unable to load users.');
    } finally {
      setLoading(false);
    }
  }, [page, search, onTotalChange]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (user) {
      fetchUsers();
    } else {
      setLoading(false);
      setItems([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
    }
  }, [user, fetchUsers]);

  const totalPages = getTotalPages(pagination.total, pagination.limit || PAGE_SIZE);
  const visiblePages = getVisiblePages(page, totalPages);
  const rangeStart = pagination.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = pagination.total === 0 ? 0 : Math.min(page * PAGE_SIZE, pagination.total);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  return (
    <>
      <div className="admin-search-bar">
        <label className="admin-search-field" htmlFor="admin-user-search">
          <FaSearch className="admin-search-icon" aria-hidden="true" />
          <input
            id="admin-user-search"
            type="text"
            className="admin-search-input"
            placeholder="Search by name, email, or phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        {searchInput ? (
          <button type="button" className="admin-search-clear" onClick={handleClearSearch}>
            Clear
          </button>
        ) : null}
      </div>

      {loading || isSearchPending ? (
        <div className="admin-state">
          <FaSpinner className="admin-spin-icon" aria-hidden="true" />
          <p>Loading users…</p>
        </div>
      ) : error ? (
        <div className="admin-state admin-state-error">
          <FaExclamationTriangle aria-hidden="true" />
          <p>{error}</p>
          <button type="button" onClick={fetchUsers}>Try again</button>
        </div>
      ) : items.length === 0 ? (
        <div className="admin-state">
          <FaClipboardList aria-hidden="true" />
          <p>{search ? `No users found for "${search}".` : 'No users found.'}</p>
        </div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table admin-users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {items.map((listedUser) => {
                  const recentlyAdded = isRecentlyAdded(listedUser);
                  return (
                    <tr key={listedUser.id} className={recentlyAdded ? 'admin-user-row-new' : undefined}>
                      <td data-label="User">
                        <div className="admin-user-cell">
                          <span className="admin-user-avatar" aria-hidden="true">
                            {(listedUser.name || listedUser.email || 'U').charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <strong>{listedUser.name || '—'}</strong>
                            {recentlyAdded ? <span className="admin-new-badge">New</span> : null}
                            <span className="admin-subtext">ID #{listedUser.id}</span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Email">{listedUser.email || '—'}</td>
                      <td data-label="Phone">{getUserPhone(listedUser) || '—'}</td>
                      <td data-label="Joined">{formatDateTime(getUserJoinedAt(listedUser))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="admin-cards">
            {items.map((listedUser) => {
              const recentlyAdded = isRecentlyAdded(listedUser);
              return (
                <article
                  key={listedUser.id}
                  className={`admin-card ${recentlyAdded ? 'admin-user-row-new' : ''}`}
                >
                  <div className="admin-card-top">
                    <div className="admin-user-cell">
                      <span className="admin-user-avatar" aria-hidden="true">
                        {(listedUser.name || listedUser.email || 'U').charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <strong>{listedUser.name || `User #${listedUser.id}`}</strong>
                        {recentlyAdded ? <span className="admin-new-badge">New</span> : null}
                        <p className="admin-subtext">{listedUser.email || '—'}</p>
                      </div>
                    </div>
                  </div>
                  <dl>
                    <div>
                      <dt>Phone</dt>
                      <dd>{getUserPhone(listedUser) || '—'}</dd>
                    </div>
                    <div>
                      <dt>Joined</dt>
                      <dd>{formatDateTime(getUserJoinedAt(listedUser))}</dd>
                    </div>
                    <div>
                      <dt>User ID</dt>
                      <dd>#{listedUser.id}</dd>
                    </div>
                  </dl>
                </article>
              );
            })}
          </div>

          <div className="admin-pagination" aria-label="Admin users pagination">
            <p className="admin-pagination-summary">
              Showing {rangeStart}–{rangeEnd} of {pagination.total} users
              {page === 1 && items.length > 0 ? ' · newest first' : ''}
            </p>
            <div className="admin-pagination-controls">
              <button
                type="button"
                className="admin-pagination-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>
              <div className="admin-pagination-pages">
                {visiblePages.map((pageNumber, index) => {
                  const prevPage = visiblePages[index - 1];
                  const showEllipsis = prevPage && pageNumber - prevPage > 1;
                  return (
                    <React.Fragment key={pageNumber}>
                      {showEllipsis ? <span className="admin-pagination-ellipsis">…</span> : null}
                      <button
                        type="button"
                        className={`admin-pagination-page ${pageNumber === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                        disabled={loading}
                        aria-current={pageNumber === page ? 'page' : undefined}
                      >
                        {pageNumber}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
              <button
                type="button"
                className="admin-pagination-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || loading || pagination.total === 0}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default AdminUsersPanel;
